import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  DocumentArrowUpIcon,
} from '@heroicons/react/24/outline';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
} from '@/components/ui/Table';

// Mock data
const mockBooks = [
  {
    id: 1,
    isbn: '978-2-123-456-7',
    title: 'Le Petit Prince',
    subtitle: 'Édition illustrée',
    language: 'fr-CA',
    status: 'PENDING',
    version: 1,
    publisher_id: 1,
    publisher_name: 'Éditions Kampus',
    cover_url: 'https://placehold.co/80x120/4AC3C3/white?text=📕',
  },
  {
    id: 2,
    isbn: '978-2-789-012-3',
    title: 'Guide voyage Paris',
    subtitle: null,
    language: 'fr-CA',
    status: 'PUBLISHED',
    version: 2,
    publisher_id: 2,
    publisher_name: 'Voyage Plus',
    cover_url: 'https://placehold.co/80x120/4CAF50/white?text=📗',
  },
  {
    id: 3,
    isbn: '978-2-345-678-9',
    title: 'Manuel technique',
    subtitle: 'Version 3.0',
    language: 'fr-CA',
    status: 'FAILED',
    version: 1,
    error_message: 'ONIX validation failed',
    publisher_id: 3,
    publisher_name: 'TechLib',
    cover_url: 'https://placehold.co/80x120/EF5350/white?text=📘',
  },
  {
    id: 4,
    isbn: '978-2-901-234-5',
    title: 'Recettes françaises',
    subtitle: 'Cuisine traditionnelle',
    language: 'fr-CA',
    status: 'PUBLISHED',
    version: 1,
    publisher_id: 1,
    publisher_name: 'Éditions Kampus',
    cover_url: 'https://placehold.co/80x120/F5C542/white?text=📙',
  },
  {
    id: 5,
    isbn: '978-2-567-890-1',
    title: 'Poésie moderne',
    subtitle: null,
    language: 'fr-CA',
    status: 'VALIDATED',
    version: 1,
    publisher_id: 1,
    publisher_name: 'Éditions Kampus',
    cover_url: 'https://placehold.co/80x120/9C27B0/white?text=📕',
  },
];

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'INGESTED', label: 'Ingested' },
  { value: 'TRANSFORMED', label: 'Transformed' },
  { value: 'VALIDATED', label: 'Validated' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'FAILED', label: 'Failed' },
];

const publisherOptions = [
  { value: '', label: 'All Publishers' },
  { value: '1', label: 'Éditions Kampus' },
  { value: '2', label: 'Voyage Plus' },
  { value: '3', label: 'TechLib' },
];

function getStatusBadge(status: string) {
  switch (status) {
    case 'PUBLISHED':
    case 'PUSHED':
    case 'PULLED_CONFIRMED':
      return <Badge variant="success" dot>{status}</Badge>;
    case 'PENDING':
    case 'INGESTED':
    case 'TRANSFORMED':
      return <Badge variant="warning" dot>{status}</Badge>;
    case 'VALIDATED':
    case 'FEED_UPDATED':
      return <Badge variant="primary" dot>{status}</Badge>;
    case 'FAILED':
    case 'CANCELLED':
      return <Badge variant="danger" dot>{status}</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
}

export default function DistBooksPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [publisherFilter, setPublisherFilter] = useState('');

  const filteredBooks = mockBooks.filter((book) => {
    const matchesSearch =
      book.title.toLowerCase().includes(search.toLowerCase()) ||
      book.isbn.includes(search);
    const matchesStatus = !statusFilter || book.status === statusFilter;
    const matchesPublisher = !publisherFilter || book.publisher_id.toString() === publisherFilter;
    return matchesSearch && matchesStatus && matchesPublisher;
  });

  const statusCounts = {
    all: mockBooks.length,
    pending: mockBooks.filter((b) => ['PENDING', 'INGESTED', 'TRANSFORMED', 'VALIDATED'].includes(b.status)).length,
    published: mockBooks.filter((b) => b.status === 'PUBLISHED').length,
    errors: mockBooks.filter((b) => b.status === 'FAILED').length,
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Book Catalog</h1>
          <p className="text-slate-500 mt-1">Manage and publish your book catalog</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" leftIcon={<DocumentArrowUpIcon className="w-4 h-4" />}>
            Build Feed
          </Button>
          <Button leftIcon={<PlusIcon className="w-4 h-4" />}>
            Add Book
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by title or ISBN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-40"
            />
            <Select
              options={publisherOptions}
              value={publisherFilter}
              onChange={(e) => setPublisherFilter(e.target.value)}
              className="w-48"
            />
          </div>
        </div>
      </Card>

      {/* Status Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setStatusFilter('')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === '' ? 'bg-primary-100 text-primary-600' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          All ({statusCounts.all})
        </button>
        <button
          onClick={() => setStatusFilter('PENDING')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'PENDING' ? 'bg-warning-100 text-warning-600' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Pending ({statusCounts.pending})
        </button>
        <button
          onClick={() => setStatusFilter('PUBLISHED')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'PUBLISHED' ? 'bg-success-100 text-success-600' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Published ({statusCounts.published})
        </button>
        <button
          onClick={() => setStatusFilter('FAILED')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            statusFilter === 'FAILED' ? 'bg-danger-100 text-danger-600' : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          Errors ({statusCounts.errors})
        </button>
      </div>

      {/* Books Table */}
      <Card padding="none">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow isHoverable={false}>
                <TableHeaderCell>Cover</TableHeaderCell>
                <TableHeaderCell>Book</TableHeaderCell>
                <TableHeaderCell>ISBN</TableHeaderCell>
                <TableHeaderCell>Publisher</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Version</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBooks.map((book) => (
                <TableRow key={book.id}>
                  <TableCell>
                    <img
                      src={book.cover_url}
                      alt={book.title}
                      className="w-12 h-16 rounded-lg object-cover shadow-sm"
                    />
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/distribution/books/${book.id}`}
                      className="hover:text-primary-500 transition-colors"
                    >
                      <p className="font-medium text-slate-800">{book.title}</p>
                      {book.subtitle && (
                        <p className="text-xs text-slate-500">{book.subtitle}</p>
                      )}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-slate-600">{book.isbn}</TableCell>
                  <TableCell className="text-slate-600">{book.publisher_name}</TableCell>
                  <TableCell>{getStatusBadge(book.status)}</TableCell>
                  <TableCell className="text-slate-600">v{book.version}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Link to={`/distribution/books/${book.id}`}>
                        <Button variant="ghost" size="sm">View</Button>
                      </Link>
                      {book.status === 'FAILED' && (
                        <Button variant="outline" size="sm" leftIcon={<ArrowPathIcon className="w-3 h-3" />}>
                          Retry
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Bulk Actions */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Showing {filteredBooks.length} of {mockBooks.length} books
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            Approve Selected
          </Button>
          <Button variant="outline" size="sm">
            Export
          </Button>
        </div>
      </div>
    </div>
  );
}

